import { Tag } from "@appkit4/react-components";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { type AiInsightsResponse, getAiInsightsAvailabilityMessage, insightTypeForRaid, useAiInsightJob } from "../../../api/aiInsights";
import { isApiError } from "../../../api/errors";
import { useGetRaidInsightItems } from "../../../api/raid";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import {
  AiInsightDeveloperContext,
  AiInsightSourceMarker,
  AiInsightsRaidTopItemCard,
} from "../AiInsightsRaidDetailParts";
import { getTagColor } from "../aiInsightTagColor";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import { AiInsightsRaidAssumptionsDetailModal } from "./AiInsightsRaidAssumptionsDetailModal";
import type {
  RaidAssumptionsInsightDto,
  RaidAssumptionsPotentialUnrecordedItemDto,
  RaidAssumptionsTopItemDto,
} from "./AiInsightsRaidAssumptionsTypes";

const RAID_ASSUMPTIONS_TAB = "assumptions" as const;

/** Backend reason code (Carter.Domain.Exceptions.NotFoundException) surfaced when the
 * current user has no active AI Insights persona assignment. */
const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";
const RAID_LINKED_ITEM_TYPES = new Set(["risk", "issue", "assumption", "decision"]);

function getRaidLookupIds(items: RaidAssumptionsTopItemDto[]): string[] {
  return items
    .flatMap((item) => [
      item.item_id,
      ...(Array.isArray(item.linked_items)
        ? item.linked_items
            .filter((linkedItem) =>
              RAID_LINKED_ITEM_TYPES.has(linkedItem.linked_item_type?.toLowerCase() ?? "")
            )
            .map((linkedItem) => linkedItem.linked_item_id)
        : []),
    ])
    .filter((itemId): itemId is string => itemId != null && itemId.trim().length > 0);
}

export type AiInsightsRaidAssumptionsShelfProps = {
  /** Whether the shared global insights overlay panel is currently open. */
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

/**
 * Renders the real, API-backed "Assumptions" RAID insight inside the global
 * insights overlay: durable request-and-poll, loading/error states, and a
 * one-time toast when the current user has
 * no AI Insights persona assigned. Mirrors AiInsightsRaidRisksIssuesShelf's
 * structure/behavior for the "assumptions" tab -- see docs/decision-log.md.
 */
export function AiInsightsRaidAssumptionsShelf({ isOpen, insightResponse }: AiInsightsRaidAssumptionsShelfProps) {
  // Only request the panel's job once the overlay is actually open.
  const assumptionsQuery = useAiInsightJob(insightTypeForRaid(RAID_ASSUMPTIONS_TAB), isOpen && insightResponse == null);

  // The assumption card currently shown in the detail popup modal, if any.
  const [selectedAssumption, setSelectedAssumption] = useState<RaidAssumptionsTopItemDto | null>(
    null
  );
  const [expandedUnrecordedIndex, setExpandedUnrecordedIndex] = useState<number | null>(null);

  const assumptionsResponse = insightResponse ?? assumptionsQuery.data?.data;
  const assumptionsInsight = assumptionsResponse?.insight as RaidAssumptionsInsightDto | undefined;
  const lookupItemIds = useMemo(
    () =>
      getRaidLookupIds(
        Array.isArray(assumptionsInsight?.top_assumptions) ? assumptionsInsight.top_assumptions : []
      ),
    [assumptionsInsight]
  );
  const resolvedRaidItemsQuery = useGetRaidInsightItems(
    lookupItemIds,
    isOpen && assumptionsInsight != null,
    assumptionsResponse?.dealDataVersionId
  );
  const resolvedRaidItemsById = new Map(
    (resolvedRaidItemsQuery.data ?? []).map((item) => [item.itemId.toLowerCase(), item])
  );
  const selectedAssumptionSourceItem = selectedAssumption?.item_id?.trim()
    ? resolvedRaidItemsById.get(selectedAssumption.item_id.trim().toLowerCase())
    : undefined;
  const isAssumptionsLoading =
    isOpen &&
    assumptionsQuery.isLoading &&
    assumptionsInsight == null;
  const assumptionsUnderlyingError = assumptionsQuery.error;
  const isPersonaNotAssignedError =
    isApiError(assumptionsUnderlyingError) &&
    assumptionsUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError = isApiError(assumptionsUnderlyingError) && assumptionsUnderlyingError.code === "no_data";
  const isReadinessWaiting = assumptionsQuery.mappingReason != null;
  const assumptionsError =
    isOpen && !isAssumptionsLoading && assumptionsInsight == null
      ? isPersonaNotAssignedError
        ? "No AI Insights persona is assigned to your account yet."
        : assumptionsQuery.isError && !isNoDataError && !isReadinessWaiting
          ? "Unable to load insights right now. Please try again shortly."
          : null
      : null;
  const assumptionsUnavailableMessage =
    isOpen && !isAssumptionsLoading && assumptionsInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(assumptionsUnderlyingError) ? assumptionsUnderlyingError.code : undefined,
          "Assumptions"
        )
      : null;

  // Surface a one-time toast when the panel is opened and the user has no
  // persona assigned, per explicit user direction: background requests must stay
  // silent (see App.tsx), but an *interactive* overlay open should tell the
  // user why no insight is rendering. Guarded by a ref so re-renders while
  // the panel stays open don't spam duplicate toasts.
  const hasShownPersonaToastRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasShownPersonaToastRef.current = false;
      return;
    }
    if (isPersonaNotAssignedError && !hasShownPersonaToastRef.current) {
      hasShownPersonaToastRef.current = true;
      toast.error(
        "No AI Insights persona is assigned to your account yet. Contact an admin to get one assigned."
      );
    }
  }, [isOpen, isPersonaNotAssignedError]);

  if (isAssumptionsLoading) {
    return (
      <section
        className="global-insights-overlay__section global-insights-overlay__section--loading"
        aria-live="polite"
      >
        <Spinner size="lg" />
      </section>
    );
  }

  if (assumptionsError != null) {
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {assumptionsError}
        </p>
      </section>
    );
  }

  if (assumptionsUnavailableMessage != null) {
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={assumptionsQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">{assumptionsUnavailableMessage}</p>
      </section>
    );
  }

  if (assumptionsInsight == null) {
    return null;
  }

  const topAssumptions = Array.isArray(assumptionsInsight.top_assumptions)
    ? assumptionsInsight.top_assumptions
    : [];
  const displayedTopAssumptions = resolvedRaidItemsQuery.isSuccess
    ? topAssumptions.filter((item) => {
        const itemId = item.item_id?.trim().toLowerCase();
        return itemId != null && itemId.length > 0 && resolvedRaidItemsById.has(itemId);
      })
    : [];
  const potentialUnrecordedAssumptions = Array.isArray(
    assumptionsInsight.potential_unrecorded_assumptions
  )
    ? assumptionsInsight.potential_unrecorded_assumptions
    : [];

  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-raid-assumptions-health-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-raid-assumptions-health-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Overall assumptions health
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          <p className="global-insights-overlay__raid-health-text">
            {assumptionsInsight.headline ?? "Not available"}
          </p>
        </div>
        {assumptionsResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={assumptionsResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-raid-assumptions-drivers-title"
      >
        <h3
          id="global-insights-raid-assumptions-drivers-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Top assumptions requiring attention
        </h3>

        {displayedTopAssumptions.length > 0 ? (
          <div className="global-insights-overlay__raid-driver-list">
            {displayedTopAssumptions.map((item) => {
              const itemId = item.item_id?.trim();
              const sourceItem = itemId
                ? resolvedRaidItemsById.get(itemId.toLowerCase())
                : undefined;
              if (!itemId || !sourceItem) return null;

              return (
                <AiInsightsRaidTopItemCard
                  key={itemId}
                  itemId={sourceItem.itemId}
                  title={sourceItem.title ?? "Assumption"}
                  owner={sourceItem.owner}
                  primaryMeta={sourceItem?.raidType}
                  secondaryMeta={sourceItem?.workstream}
                  aiGenerated
                  assumption
                  onOpen={() => setSelectedAssumption(item)}
                />
              );
            })}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">No top assumptions available.</p>
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-raid-assumptions-unrecorded-title"
      >
        <h3
          id="global-insights-raid-assumptions-unrecorded-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Potential unrecorded assumptions
        </h3>

        {potentialUnrecordedAssumptions.length > 0 ? (
          <div className="global-insights-overlay__raid-unrecorded-list">
            {potentialUnrecordedAssumptions.map((candidate, index) => (
              <UnrecordedAssumptionCard
                key={candidate?.suggested_title ?? `potential-unrecorded-assumption-${index}`}
                candidate={candidate}
                isExpanded={expandedUnrecordedIndex === index}
                onToggle={() =>
                  setExpandedUnrecordedIndex((current) => (current === index ? null : index))
                }
              />
            ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No potential unrecorded assumptions available.
          </p>
        )}
      </section>

      <AiInsightDeveloperContext developerContext={{ asAt: assumptionsInsight.as_at, dataUsed: assumptionsInsight.context_summary?.data_used, missingInputs: assumptionsInsight.context_summary?.data_missing_or_restricted, limitations: assumptionsInsight.data_quality_and_caveats }} />

      {selectedAssumption != null && selectedAssumptionSourceItem != null && (
        <AiInsightsRaidAssumptionsDetailModal
          item={selectedAssumption}
          sourceItem={selectedAssumptionSourceItem}
          asAtDate={assumptionsInsight.as_at ?? assumptionsResponse?.asAt}
          resolvedItemsById={resolvedRaidItemsById}
          onClose={() => setSelectedAssumption(null)}
        />
      )}
    </>
  );
}

type UnrecordedAssumptionCardProps = {
  candidate: RaidAssumptionsPotentialUnrecordedItemDto;
  isExpanded: boolean;
  onToggle: () => void;
};

export function UnrecordedAssumptionCard({
  candidate,
  isExpanded,
  onToggle,
}: UnrecordedAssumptionCardProps) {
  const evidenceIds = Array.isArray(candidate.evidence_ids) ? candidate.evidence_ids : [];
  const possibleMatch = candidate.possible_existing_assumption_match?.trim();
  const possibleMatchIds = [...new Set(possibleMatch?.match(/\b[A-Z]+-\d+\b/g) ?? [])];
  const workstream = candidate.likely_relevant_workstream?.trim();
  const workstreamTags = [
    ...new Set((workstream?.split("/") ?? []).map((part) => part.trim()).filter(Boolean)),
  ];

  return (
    <div className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">
            {`Confidence: ${candidate.confidence ?? "Not available"}`}
          </p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">
            {candidate.suggested_title ?? "Not available"}
          </h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>

      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-label">Why flagged</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.why_flagged ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Condition relied upon</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.condition_relied_upon ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">
            Possible impact if incorrect
          </p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.possible_impact_if_incorrect ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Validation question</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.validation_question ?? "Not available"}
          </p>
          {(possibleMatchIds.length > 0 || workstreamTags.length > 0) && (
            <>
              <hr className="global-insights-overlay__raid-unrecorded-divider" />
              <div className="global-insights-overlay__raid-unrecorded-tags">
                {possibleMatchIds.length > 0 && <div>Possible match:</div>}
                {possibleMatchIds.map((possibleMatchId) => (
                  <Tag
                    key={possibleMatchId}
                    label={possibleMatchId}
                    type="outlined"
                    size="sm"
                    closable={false}
                    color={getTagColor(possibleMatchId)}
                  />
                ))}
                {workstreamTags.map((workstreamTag) => (
                  <Tag
                    key={workstreamTag}
                    label={workstreamTag}
                    type="outlined"
                    size="sm"
                    closable={false}
                    color={getTagColor(workstreamTag)}
                  />
                ))}
              </div>
            </>
          )}
          {evidenceIds.length > 0 && (
            <p className="global-insights-overlay__raid-unrecorded-text">{`Evidence: ${evidenceIds.join(", ")}`}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AiInsightsRaidAssumptionsShelf;
